import {
  SPACEMOUSE_VENDOR_ID,
  SPACEMOUSE_PRODUCT_IDS,
  REPORT_IDS,
  DEFAULT_CONFIG,
  MAX_AXIS_VALUE,
  ZOOM_THRESHOLD,
} from "./spacemouse.constants";
import {
  SpaceMouseState,
  SpaceMouseConfig,
  SpaceMouseCallbacks,
  SpaceMouseConnectionStatus,
  SpaceMouseDeviceInfo,
} from "./spacemouse.types";

export class SpaceMouseService {
  private device: HIDDevice | null = null;
  private config: SpaceMouseConfig;
  private callbacks: SpaceMouseCallbacks;
  private state: SpaceMouseState;
  private animationFrameId: number | null = null;
  private zoomAccumulator: number = 0;
  private rotateAccumulator: number = 0;
  private lastRotateButton: number = 0;

  public status: SpaceMouseConnectionStatus = "disconnected";

  constructor(
    config: Partial<SpaceMouseConfig> = {},
    callbacks: SpaceMouseCallbacks = {}
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.callbacks = callbacks;
    this.state = this.createInitialState();

    if (!SpaceMouseService.isSupported()) {
      this.status = "unsupported";
    }
  }

  private createInitialState(): SpaceMouseState {
    return {
      panX: 0,
      panY: 0,
      panZ: 0,
      rotateX: 0,
      rotateY: 0,
      rotateZ: 0,
      buttons: new Set(),
    };
  }

  public static isSupported(): boolean {
    return typeof navigator !== "undefined" && "hid" in navigator;
  }

  public async tryAutoConnect(): Promise<boolean> {
    if (this.status === "unsupported" || this.device) {
      return this.status === "connected";
    }

    try {
      const devices = await navigator.hid.getDevices();
      const spaceMouse = devices.find(
        (d) => d.vendorId === SPACEMOUSE_VENDOR_ID
      );

      if (spaceMouse) {
        return this.connectToDevice(spaceMouse);
      }
    } catch (error) {
      console.log("[SpaceMouse] Auto-connect check failed:", error);
    }

    return false;
  }

  public async connect(): Promise<boolean> {
    if (this.status === "unsupported") {
      return false;
    }

    if (this.device) {
      return true;
    }

    this.status = "connecting";

    try {
      const filters = [
        { vendorId: SPACEMOUSE_VENDOR_ID },
        ...Object.values(SPACEMOUSE_PRODUCT_IDS).map((productId) => ({
          vendorId: SPACEMOUSE_VENDOR_ID,
          productId,
        })),
      ];

      const devices = await navigator.hid.requestDevice({ filters });

      if (devices.length === 0) {
        this.status = "disconnected";
        return false;
      }

      return this.connectToDevice(devices[0]);
    } catch (error) {
      this.status = "error";
      return false;
    }
  }

  private async connectToDevice(device: HIDDevice): Promise<boolean> {
    this.device = device;
    this.status = "connecting";

    if (!this.device.opened) {
      try {
        await this.device.open();
      } catch (openError) {
        console.error(
          "[SpaceMouse] Failed to open device. The 3DConnexion driver likely has exclusive access.",
          "\n\nTo fix: Quit the 3DConnexion app/driver, unplug and replug the device, then try again.",
          openError
        );
        this.status = "error";
        this.device = null;
        return false;
      }
    }

    this.device.oninputreport = this.handleInputReport;

    this.status = "connected";
    this.callbacks.onConnect?.();
    this.startUpdateLoop();

    return true;
  }

  public async disconnect(): Promise<void> {
    this.stopUpdateLoop();

    if (this.device) {
      this.device.oninputreport = null;

      if (this.device.opened) {
        await this.device.close();
      }

      this.device = null;
    }

    this.state = this.createInitialState();
    this.status = "disconnected";
    this.callbacks.onDisconnect?.();
  }

  private handleInputReport = (event: HIDInputReportEvent): void => {
    const { reportId, data } = event;

    switch (reportId) {
      case REPORT_IDS.TRANSLATION:
        this.state.panX = data.getInt16(0, true);
        this.state.panY = data.getInt16(2, true);
        this.state.panZ = data.getInt16(4, true);
        break;

      case REPORT_IDS.ROTATION:
        this.state.rotateX = data.getInt16(0, true);
        this.state.rotateY = data.getInt16(2, true);
        this.state.rotateZ = data.getInt16(4, true);
        break;

      case REPORT_IDS.BUTTONS:
        this.handleButtonReport(data);
        break;

      default:
        if (data.byteLength >= 12) {
          this.state.panX = data.getInt16(0, true);
          this.state.panY = data.getInt16(2, true);
          this.state.panZ = data.getInt16(4, true);
          this.state.rotateX = data.getInt16(6, true);
          this.state.rotateY = data.getInt16(8, true);
          this.state.rotateZ = data.getInt16(10, true);
        } else if (data.byteLength >= 6) {
          this.state.panX = data.getInt16(0, true);
          this.state.panY = data.getInt16(2, true);
          this.state.panZ = data.getInt16(4, true);
        }
        break;
    }
  };

  private handleButtonReport(data: DataView): void {
    const previousButtons = new Set(this.state.buttons);
    this.state.buttons.clear();

    const buttonByte = data.getUint8(0);

    for (let i = 0; i < 8; i++) {
      if (buttonByte & (1 << i)) {
        const buttonNum = i + 1;
        this.state.buttons.add(buttonNum);
      }
    }

    previousButtons.forEach((button) => {
      if (!this.state.buttons.has(button)) {
        this.callbacks.onButtonRelease?.(button);
      }
    });
  }

  private applyDeadZone(value: number, type: "pan" | "zoom" = "pan"): number {
    const { deadZone, zoomDeadZone } = this.config;
    const effectiveDeadZone = type === "zoom" ? zoomDeadZone : deadZone;

    if (Math.abs(value) < effectiveDeadZone) {
      return 0;
    }

    const sign = Math.sign(value);
    const magnitude = Math.abs(value) - effectiveDeadZone;

    return sign * magnitude;
  }

  private normalizeAxis(value: number): number {
    const adjusted = this.applyDeadZone(value);
    return Math.max(
      -1,
      Math.min(1, adjusted / (MAX_AXIS_VALUE - this.config.deadZone))
    );
  }

  private applyPanEasing(normalizedValue: number): number {
    if (normalizedValue === 0) return 0;

    const sign = Math.sign(normalizedValue);
    const magnitude = Math.abs(normalizedValue);

    // Cubic easing with 3x base speed and 6x speed at apex
    const easedMagnitude = Math.pow(magnitude, 3) * (3 + 3 * magnitude);

    return sign * easedMagnitude;
  }

  private startUpdateLoop(): void {
    const update = () => {
      this.processState();
      this.animationFrameId = requestAnimationFrame(update);
    };
    this.animationFrameId = requestAnimationFrame(update);
  }

  private stopUpdateLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private processState(): void {
    const {
      panSensitivity,
      zoomSensitivity,
      invertPanX,
      invertPanY,
      invertZoom,
    } = this.config;

    const panX =
      this.applyPanEasing(this.normalizeAxis(this.state.panX)) *
      panSensitivity *
      (invertPanX ? -1 : 1);
    const panY =
      this.applyPanEasing(this.normalizeAxis(this.state.panY)) *
      panSensitivity *
      (invertPanY ? -1 : 1);

    if (panX !== 0 || panY !== 0) {
      this.callbacks.onPan?.(panX, panY);
    }

    const panZ = this.applyDeadZone(this.state.panZ, "zoom");
    if (panZ !== 0) {
      this.zoomAccumulator += panZ * zoomSensitivity * (invertZoom ? -1 : 1);

      if (Math.abs(this.zoomAccumulator) > ZOOM_THRESHOLD) {
        const direction = Math.sign(this.zoomAccumulator);
        this.callbacks.onZoom?.(direction);
        this.zoomAccumulator = 0;
      }
    } else {
      this.zoomAccumulator = 0;
    }

    // Twist rotation (rotateZ) - currently unused
    const _rotateZ = this.applyDeadZone(this.state.rotateZ);
    if (_rotateZ !== 0) {
      // noop - can be wired to a callback in the future
      // e.g., this.callbacks.onTwist?.(_rotateZ);
    }

    // Tilt rotation (rotateX, rotateY) - explicitly disabled for viewer use
    // These values are read from the device but intentionally ignored
    // to prevent unwanted tilt-based interactions in the image viewer
    const _rotateX = this.applyDeadZone(this.state.rotateX);
    const _rotateY = this.applyDeadZone(this.state.rotateY);
    // Explicitly do nothing with tilt values
    // if (_rotateX !== 0 || _rotateY !== 0) {
    //   this.callbacks.onTilt?.(_rotateX, _rotateY);
    // }

    const ROTATE_INTERVAL_MS = 100;
    const ROTATE_THRESHOLD = ROTATE_INTERVAL_MS / (1000 / 60);

    if (this.state.buttons.has(1)) {
      if (this.lastRotateButton !== 1) {
        this.callbacks.onButtonPress?.(1);
        this.lastRotateButton = 1;
        this.rotateAccumulator = 0;
      } else {
        this.rotateAccumulator += 1;
        if (this.rotateAccumulator >= ROTATE_THRESHOLD) {
          this.callbacks.onButtonPress?.(1);
          this.rotateAccumulator = 0;
        }
      }
    } else if (this.state.buttons.has(2)) {
      if (this.lastRotateButton !== 2) {
        this.callbacks.onButtonPress?.(2);
        this.lastRotateButton = 2;
        this.rotateAccumulator = 0;
      } else {
        this.rotateAccumulator += 1;
        if (this.rotateAccumulator >= ROTATE_THRESHOLD) {
          this.callbacks.onButtonPress?.(2);
          this.rotateAccumulator = 0;
        }
      }
    } else {
      this.lastRotateButton = 0;
      this.rotateAccumulator = 0;
    }
  }

  public setConfig(config: Partial<SpaceMouseConfig>): void {
    this.config = { ...this.config, ...config };
  }

  public getConfig(): SpaceMouseConfig {
    return { ...this.config };
  }

  public getState(): SpaceMouseState {
    return { ...this.state, buttons: new Set(this.state.buttons) };
  }

  public isConnected(): boolean {
    return this.status === "connected" && this.device !== null;
  }

  public getDeviceInfo(): SpaceMouseDeviceInfo | null {
    if (!this.device) return null;
    return {
      name: this.device.productName || "Unknown Space Mouse",
      vendorId: this.device.vendorId,
      productId: this.device.productId,
    };
  }
}

let serviceInstance: SpaceMouseService | null = null;

export function getSpaceMouseService(): SpaceMouseService {
  if (!serviceInstance) {
    serviceInstance = new SpaceMouseService();
  }
  return serviceInstance;
}

export function createSpaceMouseService(
  config?: Partial<SpaceMouseConfig>,
  callbacks?: SpaceMouseCallbacks
): SpaceMouseService {
  serviceInstance = new SpaceMouseService(config, callbacks);
  return serviceInstance;
}
